type GraphQLError = { message?: string, errorType?: string };

// What an operator sees when billing-api refuses an update by its condition. The
// condition (catalog/freeze.ts, FNP-838) fails when the unit has left DRAFT and the
// save changes meterId or the factor, or when the unit no longer exists. The API
// reports both as a raw DynamoDB condition failure, so the sentence names both.
export const FROZEN_UNIT_MESSAGE =
    'Billing refused this change. A billable unit\'s meter and metered units per billable unit are frozen ' +
    'once it leaves Draft, and this unit is no longer a draft (or it no longer exists). ' +
    'To reprice, create a new billable unit and point the rate plans at it.';

// When a mutation resolver fails, AppSync also reports every non-null field of the null
// result. Those follow-ons describe the failure's side effect, not its cause.
const NULL_RESULT_FOLLOW_ON = /^Cannot return null for non-nullable type/;

// Collapse a rejected billing-api mutation to ONE error with a readable message, so the
// shared notifyError shows one toast instead of one per GraphQL error. Anything that is
// not a GraphQL error list (auth, network) passes through unchanged, so notifyError's
// session-expiry handling still sees it.
export const toOperatorError = (e: unknown, operation: 'create' | 'update' | 'delete'): unknown => {
    const errors = (e as { errors?: GraphQLError[] } | null)?.errors;
    if (!Array.isArray(errors) || errors.length === 0) {
        return e;
    }

    if (operation === 'update'
        && errors.some(error => error.errorType?.includes('ConditionalCheckFailedException'))) {
        return new Error(FROZEN_UNIT_MESSAGE);
    }

    const cause = errors.find(error => !NULL_RESULT_FOLLOW_ON.test(error.message ?? '')) ?? errors[0];
    return new Error(cause.message || 'Error performing the operation');
};
